import { AlertTriangle, Clock, Coffee, Hourglass, Navigation, Sparkles } from 'lucide-react';
import { recomputeStopSequence } from '../../lib/job-plan-sequence';
import { formatOffsetTime } from '../../lib/job-plan-time';
import type { Job, OperatorProfile, RouteAssignment } from '../../types/job-plan';
import { Badge } from '../../ui/badge';

interface JobPlanTimelineProps {
  assignments: RouteAssignment[];
  operators: OperatorProfile[];
  onSelectJob?: (job: Job) => void;
}

const TIMELINE_START_HOUR = 7; // 07:00
const TIMELINE_END_HOUR = 19; // 19:00
const TIMELINE_TOTAL_SECONDS = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 3600;

export function JobPlanTimeline({ assignments, operators, onSelectJob }: JobPlanTimelineProps) {
  const hours = Array.from(
    { length: TIMELINE_END_HOUR - TIMELINE_START_HOUR + 1 },
    (_, i) => TIMELINE_START_HOUR + i
  );

  const getPercent = (offsetSeconds: number) => {
    const timelineStartSec = TIMELINE_START_HOUR * 3600;
    const relSec = offsetSeconds - timelineStartSec;
    return Math.max(0, Math.min(100, (relSec / TIMELINE_TOTAL_SECONDS) * 100));
  };

  return (
    <div className="w-full h-full flex flex-col bg-card rounded-xl border border-border/60 shadow-sm overflow-hidden">
      {/* Time Header Axis */}
      <div className="flex border-b border-border/40 bg-muted/40 text-[11px] font-mono text-muted-foreground select-none">
        <div className="w-56 shrink-0 p-2.5 font-semibold text-foreground border-r border-border/40">
          Operator & Productivity
        </div>
        <div className="flex-1 relative flex h-9">
          {hours.map((h) => {
            const pct = getPercent(h * 3600);
            return (
              <div
                key={h}
                className="absolute top-0 bottom-0 border-l border-border/30 px-1 pt-1.5"
                style={{ left: `${pct}%` }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            );
          })}
        </div>
      </div>

      {/* Operator Rows */}
      <div className="flex-1 overflow-y-auto divide-y divide-border/30">
        {operators.length === 0 ? (
          <div className="p-8 text-center text-xs text-muted-foreground">
            No active operators available for timeline display.
          </div>
        ) : (
          operators.map((op) => {
            const assignment = assignments.find((a) => a.operatorProfileId === op.id);
            const stops = assignment?.stops ?? [];
            const sequenceInputs = stops.map((s) => ({
              id: s.id,
              jobId: s.jobId,
              durationSeconds: s.job?.estimatedDurationSeconds ?? 3600,
              latitude: s.job?.latitude,
              longitude: s.job?.longitude,
              windowStartOffsetSeconds: s.job?.windowStartOffsetSeconds,
              windowEndOffsetSeconds: s.job?.windowEndOffsetSeconds,
              plannedArrival: s.plannedArrival,
              plannedDeparture: s.plannedDeparture,
            }));

            const feasibility = recomputeStopSequence(sequenceInputs, {
              shiftStartOffsetSeconds: op.shiftStartOffsetSeconds,
              shiftEndOffsetSeconds: op.shiftEndOffsetSeconds,
              breakDurationSeconds: op.breakDurationSeconds,
              breakWindowStartOffsetSeconds: op.breakWindowStartOffsetSeconds,
              breakWindowEndOffsetSeconds: op.breakWindowEndOffsetSeconds,
              startLocation: op.startLocation,
              endLocation: op.endLocation,
            });

            const shiftStartPct = getPercent(op.shiftStartOffsetSeconds);
            const shiftEndPct = getPercent(op.shiftEndOffsetSeconds);
            const shiftWidthPct = Math.max(0, shiftEndPct - shiftStartPct);

            const productivityPct = Math.round(feasibility.productivityRate * 100);
            const serviceHours = (feasibility.totalServiceSeconds / 3600).toFixed(1);
            const driveMins = Math.round(feasibility.totalDriveSeconds / 60);
            const waitMins = Math.round(feasibility.totalWaitingSeconds / 60);

            return (
              <div key={op.id} className="flex hover:bg-muted/10 transition-colors">
                {/* Operator Profile & Productivity Metric Column */}
                <div className="w-56 shrink-0 p-3 border-r border-border/40 flex flex-col gap-1.5 justify-center bg-card">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-semibold text-xs truncate">
                      {op.user?.fullName || op.user?.email || `Operator #${op.id}`}
                    </span>
                    <Badge
                      variant="secondary"
                      className={`text-[9px] py-0 px-1 font-mono ${
                        productivityPct >= 65
                          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                          : 'bg-primary/10 text-primary border-primary/20'
                      }`}
                    >
                      <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                      {productivityPct}% Util
                    </Badge>
                  </div>

                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>
                      {formatOffsetTime(op.shiftStartOffsetSeconds)} –{' '}
                      {formatOffsetTime(op.shiftEndOffsetSeconds)}
                    </span>
                  </div>

                  {/* Utilization Breakdown Pills */}
                  <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                    <span className="text-primary font-medium">{serviceHours}h work</span>
                    <span>•</span>
                    <span className="text-amber-600 dark:text-amber-400 font-medium">
                      {driveMins}m drive
                    </span>
                    {waitMins > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                          {waitMins}m idle
                        </span>
                      </>
                    )}
                  </div>

                  {feasibility.isOverShift && (
                    <Badge variant="destructive" className="text-[9px] py-0 px-1 w-fit">
                      +{Math.round(feasibility.overrunSeconds / 60)}m Overrun
                    </Badge>
                  )}
                </div>

                {/* Timeline Visual Track */}
                <div className="flex-1 relative h-24 bg-muted/5 flex items-center overflow-hidden">
                  {/* Grid Lines */}
                  {hours.map((h) => (
                    <div
                      key={h}
                      className="absolute top-0 bottom-0 border-l border-border/15 pointer-events-none"
                      style={{ left: `${getPercent(h * 3600)}%` }}
                    />
                  ))}

                  {/* Shift Base Track */}
                  <div
                    className="absolute top-2 bottom-2 bg-muted/30 rounded-lg border border-border/40 pointer-events-none"
                    style={{ left: `${shiftStartPct}%`, width: `${shiftWidthPct}%` }}
                  />

                  {/* Reserved Break Window Background */}
                  {op.breakDurationSeconds > 0 && (
                    <div
                      className="absolute top-3 bottom-3 bg-emerald-500/5 border border-dashed border-emerald-500/20 rounded flex items-center justify-center text-[10px] text-emerald-600/70 dark:text-emerald-400/70 font-mono"
                      style={{
                        left: `${getPercent(op.breakWindowStartOffsetSeconds)}%`,
                        width: `${Math.max(
                          2,
                          getPercent(op.breakWindowEndOffsetSeconds) -
                            getPercent(op.breakWindowStartOffsetSeconds)
                        )}%`,
                      }}
                      title="Reserved Break Window"
                    >
                      <Coffee className="h-3 w-3 opacity-40" />
                    </div>
                  )}

                  {/* Travel & Dead Time Segments + Stops */}
                  {feasibility.stops.map((stopTiming, idx) => {
                    const stop = stops.find((s) => s.jobId === stopTiming.jobId);
                    const job = stop?.job;
                    if (!job) return null;

                    // 1. Travel / Drive Time segment before this stop
                    const hasDrive = stopTiming.driveSecondsFromPrevious > 0;
                    const driveStartPct = getPercent(stopTiming.driveStartSeconds);
                    const driveEndPct = getPercent(stopTiming.plannedArrivalSeconds);
                    const driveWidthPct = Math.max(0.8, driveEndPct - driveStartPct);

                    // 2. Waiting / Idle gap (if operator arrived early before customer window)
                    const hasWait = stopTiming.waitingSeconds > 0;
                    const waitStartPct = getPercent(stopTiming.plannedArrivalSeconds);
                    const waitEndPct = getPercent(stopTiming.serviceStartSeconds);
                    const waitWidthPct = Math.max(0.8, waitEndPct - waitStartPct);

                    // 3. Service Work Block
                    const serviceStartPct = getPercent(stopTiming.serviceStartSeconds);
                    const serviceEndPct = getPercent(stopTiming.plannedDepartureSeconds);
                    const serviceWidthPct = Math.max(1.5, serviceEndPct - serviceStartPct);

                    return (
                      <div key={stopTiming.jobId}>
                        {/* Travel / Transit Block */}
                        {hasDrive && (
                          <div
                            className="absolute top-7 bottom-7 rounded bg-amber-500/20 border border-dashed border-amber-500/40 text-amber-700 dark:text-amber-300 flex items-center justify-center text-[9px] font-mono z-0 transition-opacity hover:opacity-90 cursor-default"
                            style={{
                              left: `${driveStartPct}%`,
                              width: `${driveWidthPct}%`,
                            }}
                            title={`Travel to Stop #${idx + 1}: ${Math.round(
                              stopTiming.driveSecondsFromPrevious / 60
                            )} min drive (${formatOffsetTime(
                              stopTiming.driveStartSeconds
                            )} – ${formatOffsetTime(stopTiming.plannedArrivalSeconds)})`}
                          >
                            <div className="flex items-center gap-0.5 px-1 truncate">
                              <Navigation className="h-2.5 w-2.5 shrink-0 rotate-45 text-amber-600 dark:text-amber-400" />
                              <span className="truncate text-[8.5px] font-medium">
                                {Math.round(stopTiming.driveSecondsFromPrevious / 60)}m
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Idle / Waiting Gap Block */}
                        {hasWait && (
                          <div
                            className="absolute top-7 bottom-7 rounded bg-yellow-500/25 border border-dashed border-yellow-500/50 text-yellow-800 dark:text-yellow-200 flex items-center justify-center text-[9px] font-mono z-0 transition-opacity hover:opacity-90 cursor-default"
                            style={{
                              left: `${waitStartPct}%`,
                              width: `${waitWidthPct}%`,
                            }}
                            title={`Waiting for Time Window to open: ${Math.round(
                              stopTiming.waitingSeconds / 60
                            )} min idle (${formatOffsetTime(
                              stopTiming.plannedArrivalSeconds
                            )} – ${formatOffsetTime(stopTiming.serviceStartSeconds)})`}
                          >
                            <div className="flex items-center gap-0.5 px-1 truncate">
                              <Hourglass className="h-2.5 w-2.5 shrink-0 text-yellow-600 dark:text-yellow-400" />
                              <span className="truncate text-[8.5px] font-medium">
                                {Math.round(stopTiming.waitingSeconds / 60)}m wait
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Service Job Card */}
                        <div
                          onClick={() => onSelectJob?.(job)}
                          className={`absolute top-2.5 bottom-2.5 rounded-lg px-2 flex items-center justify-between text-xs font-semibold border-2 shadow-xs transition-transform hover:scale-[1.02] cursor-pointer z-10 ${
                            stopTiming.windowViolation
                              ? 'bg-red-500/20 border-red-500/60 text-red-700 dark:text-red-300'
                              : 'bg-primary/20 border-primary/50 text-foreground'
                          }`}
                          style={{
                            left: `${serviceStartPct}%`,
                            width: `${serviceWidthPct}%`,
                          }}
                          title={`${job.title} (${formatOffsetTime(
                            stopTiming.serviceStartSeconds
                          )} - ${formatOffsetTime(stopTiming.plannedDepartureSeconds)}) • ${Math.round(
                            job.estimatedDurationSeconds / 60
                          )}m service duration`}
                        >
                          <span className="truncate text-[11px] font-semibold">
                            #{idx + 1} {job.title}
                          </span>
                          {stopTiming.windowViolation && (
                            <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 ml-1" />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Scheduled Break Actual Placement */}
                  {feasibility.breakStartOffsetSeconds != null &&
                    feasibility.breakEndOffsetSeconds != null && (
                      <div
                        className="absolute top-4 bottom-4 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-[10px] font-medium z-10 select-none shadow-xs"
                        style={{
                          left: `${getPercent(feasibility.breakStartOffsetSeconds)}%`,
                          width: `${Math.max(
                            1.5,
                            getPercent(feasibility.breakEndOffsetSeconds) -
                              getPercent(feasibility.breakStartOffsetSeconds)
                          )}%`,
                        }}
                        title={`Scheduled Break: ${Math.round(
                          op.breakDurationSeconds / 60
                        )} mins (${formatOffsetTime(
                          feasibility.breakStartOffsetSeconds
                        )} – ${formatOffsetTime(feasibility.breakEndOffsetSeconds)})`}
                      >
                        <div className="flex items-center gap-1 px-1.5 truncate">
                          <Coffee className="h-3 w-3 shrink-0" />
                          <span className="truncate text-[9.5px]">
                            Break ({Math.round(op.breakDurationSeconds / 60)}m)
                          </span>
                        </div>
                      </div>
                    )}

                  {/* Return Drive to Depot (after last stop) */}
                  {feasibility.returnDriveSeconds > 0 &&
                    feasibility.returnDriveStartSeconds != null &&
                    feasibility.returnDriveEndSeconds != null && (
                      <div
                        className="absolute top-7 bottom-7 rounded bg-amber-500/20 border border-dashed border-amber-500/40 text-amber-700 dark:text-amber-300 flex items-center justify-center text-[9px] font-mono z-0"
                        style={{
                          left: `${getPercent(feasibility.returnDriveStartSeconds)}%`,
                          width: `${Math.max(
                            0.8,
                            getPercent(feasibility.returnDriveEndSeconds) -
                              getPercent(feasibility.returnDriveStartSeconds)
                          )}%`,
                        }}
                        title={`Return to End Depot: ${Math.round(
                          feasibility.returnDriveSeconds / 60
                        )} min drive (${formatOffsetTime(
                          feasibility.returnDriveStartSeconds
                        )} – ${formatOffsetTime(feasibility.returnDriveEndSeconds)})`}
                      >
                        <div className="flex items-center gap-0.5 px-1 truncate">
                          <Navigation className="h-2.5 w-2.5 shrink-0 rotate-45 text-amber-600 dark:text-amber-400" />
                          <span className="truncate text-[8.5px] font-medium">
                            {Math.round(feasibility.returnDriveSeconds / 60)}m return
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Timeline Legend Footer */}
      <div className="border-t border-border/40 p-2.5 bg-muted/20 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-4 rounded bg-primary/20 border-2 border-primary/50" />
            <span>Job Service (Productive)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-4 rounded bg-amber-500/20 border border-dashed border-amber-500/40 flex items-center justify-center">
              <Navigation className="h-2 w-2 text-amber-600 dark:text-amber-400 rotate-45" />
            </div>
            <span>Travel / Driving (Transit)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-4 rounded bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <Coffee className="h-2 w-2 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span>Scheduled Break</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-4 rounded bg-yellow-500/25 border border-dashed border-yellow-500/50 flex items-center justify-center">
              <Hourglass className="h-2 w-2 text-yellow-600 dark:text-yellow-400" />
            </div>
            <span>Waiting / Dead Time</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-4 rounded bg-muted/30 border border-border/40" />
            <span>Available Shift Capacity</span>
          </div>
        </div>
      </div>
    </div>
  );
}
